// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.AccessStore.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { action, observable } from 'mobx';

import CONSTANT from 'logic/data/Izenda.AccessConstants';
import reportFetcher from 'fetchers/Izenda.ReportsDataFetcher';
import accessFetcher from 'fetchers/Izenda.AccessDataFetcher';
import dashboardFetcher from 'fetchers/Izenda.DashboardDataFetcher';
import uiState from 'logic/Izenda.UiState';
import { map, isNil, isEmpty } from 'lodash/fp';
import TenantManagementStore from './Izenda.TenantManagementStore';

export default class AccessStore {
  @observable model: any = null;
  @observable documentId: string = '';
  @observable accessMode: any = null;
  @observable accessFor: number = 0;
  @observable roles: Array<any> = []; // List available role can grant access
  @observable users: Array<any> = []; // List available user can grant access
  @observable tenantId: any = uiState.getTenantId();

  constructor(model: any, documentId: any, accessMode: any) {
    this.isGlobal = false;
    this.model = model;
    this.documentId = documentId;
    this.accessMode = accessMode;
    this.loadAccessTenantHierarchy().then(x => {});
  }

  /**
   * Load tenant/tenant groups hierarchy info.
   * @returns {Promise}
   */
  loadAccessTenantHierarchy() {
    return TenantManagementStore
      .getTenantGroupsHierarchy([])
      .then(tenantHierarchy => {
        if(!isNil(this.model))
          this.model.tenantHierarchy = tenantHierarchy;
        return tenantHierarchy;
      });

  }

  /**
   * Load access for search/sort...
   * @param {string}    report Id
   * @param {object}    access data
   * @return {object} Promise
   */
  loadAllAccess(documentId: string, data: any): any {
    const model = this.model;
    model.pageInfo.buildPagingRequest();
    const dataFetcher = this.accessMode === CONSTANT.ACCESS_MODE.DASHBOARD ? dashboardFetcher : reportFetcher;

    return dataFetcher.loadAccesses(documentId, data, model.pageInfo).then(
      action(access => {
        this.model.populateAccessDetails(access);
        return Promise.resolve(true);
      })
    );
  }

  /**
   * Handle when page size change
   * @param {int}    page number
   * @return {object} Promise
   */
  onPageSizeChange(pageSize: any): any {
    this.model.pageInfo.setPageSize(pageSize);
    return this.goToPage(1);
  }

  /**
   * Handle go to page
   * @param  {int} pageIndex
   * @return {object} Promise
   */
  goToPage(pageIndex: number): any {
    const pageInfo = this.model.pageInfo;
    pageInfo.setPageIndex(pageIndex);
    this.model.mergeWithReportDraftAccess();
    return this.loadAllAccess(this.documentId, this.model.draftAccesses);
  }

  /**
   * Search subscription
   * @param  {string} searchProperty
   * @param  {string} searchText
   * @return {object} Promise
   */
  search(searchProperty: string, searchText: string): any {
    this.model.pageInfo.setSearchValues(searchProperty, searchText);
    return this.goToPage(1);
  }

  /**
   * Change sort orders
   * @param {string} columnKey
   * @param {object} sortDir
   * @return {object} Promise
   */
  onSortChange(columnKey: string, sortDir: any): any {
    this.model.pageInfo.setColSortDirs(columnKey, sortDir);
    this.model.mergeWithReportDraftAccess();
    return this.loadAllAccess(this.documentId, this.model.draftAccesses);
  }

  /**
   * Update filter criteria information.
   * @param {String} searchProperty
   * @param {String} searchText
   */
  updateSearchCriteria(searchProperty: string, searchText: string) {
    this.model.pageInfo.setSearchValues(searchProperty, searchText);
  }

  /**
   * Get all data for report/dashboard access page
   * @param  {string} documentId report id
   * @param  {object} data        current access
   * @return {object}             Promise
   */
  initialAccessReportPage(documentId: string, data: any, isGlobal: boolean): any {
    const model = this.model;
    model.pageInfo.buildPagingRequest();
    this.isGlobal = isGlobal;

    return Promise.join(
      accessFetcher.loadAccessRight(CONSTANT.ACCESS_MODE.REPORT),
      reportFetcher.loadAccesses(documentId, data, model.pageInfo),
      action((accessRights, access) => {
        this.model.populateAccessDetails(access);
        this.model.populateAccessRight(accessRights);
      })
    );
  }

  /**
   * Update report/dashboardId Id after save or save draft report
   * @param  {string} documentId report/dashboard id
   */
  updateReporId(documentId: string) {
    this.documentId = documentId;
  }

  /**
   * Load data for dashboard
   * @param  {string}   dashboarddId
   * @param  {string}   accessMode
   * @param  {boolean}  isGlobal
   * @param  {boolean}  isNew
   * @return {object}   Promise result
   */
  @action
  async initialAccessDashboardPage(dashboardId: string, accessMode: boolean, isGlobal: boolean, isNew: boolean): void {
    const model = this.model;
    model.pageInfo.buildPagingRequest();
    this.isGlobal = isGlobal;
    this.documentId = dashboardId;

    const fetcherFn = isNew
      ? accessFetcher.loadDefaultAccess(1)
      : dashboardFetcher.loadAccesses(dashboardId, null, model.pageInfo);

    const [access, accessRights] = await Promise.all([
      fetcherFn,
      accessFetcher.loadAccessRight(CONSTANT.ACCESS_MODE.DASHBOARD)
    ]);

    const result = isNew ? { result: access } : access;
    model.populateAccessRight(accessRights);
    model.populateAccessDetails(result);
  }

  /**
   * Populate roles list
   * @param  {array} roles
   */
  populateRoles(roles: Array<any>) {
    this.roles = map(
      role => ({
        id: role.id,
        name: role.name,
        notAllowSharing: role.notAllowSharing,
        tenantId: role.tenantId
      }),
      roles
    );
  }

  /**
   * Populate avaiable users can grant access
   * @param  {array} users
   */
  populateUsers(users: Array<any>) {
    this.users = map(user => {
      const searchName =
        isNil(user.emailAddress) || isEmpty(user.emailAddress)
          ? String.format('{0} {1}', user.firstName, user.lastName)
          : String.format('{0} {1} ({2})', user.firstName, user.lastName, user.emailAddress);

      return {
        id: user.id,
        userName: user.userName,
        firstName: user.firstName,
        lastName: user.lastName,
        fullName: String.format('{0} {1}', user.firstName, user.lastName),
        searchName,
        emailAddress: user.emailAddress,
        notAllowSharing: user.notAllowSharing,
        isCurrentUser: user.id === uiState.currentUser.id
      };
    }, users);
  }

  /**
   * Validate access list when change global checkbox
   * @param  {Boolean}  isGlobal
   * @param  {Boolean}  isReport
   * @return {Boolean or String}  result or warning message
   */
  validateAccessListOnUpdateGlobalReport(isGlobal: boolean, isReport: boolean): boolean {
    const model = this.model;
    this.isGlobal = isGlobal;

    if (isGlobal && model.hasAsLeastAccessHasUserSelect) {
      return isReport ? CONSTANT.MESSAGE.REMOVE_USER : CONSTANT.MESSAGE.REMOVE_DASHBOARD_USER;
    }

    if (!isGlobal && model.hasAsLeastAccessHasRoleSelect) {
      return isReport ? CONSTANT.MESSAGE.REMOVE_ROLE : CONSTANT.MESSAGE.REMOVE_DASHBOARD_ROLE;
    }

    return false;
  }

  /**
   * Update list accessor
   * @param{Boolean} isGlobal
   */
  @action
  updateListAccessor(isGlobal: boolean) {
    // Clear all sharing with user if change to global
    isGlobal && this.model.clearAllUserAccessor();

    // Clean all global roles on access detail.
    !isGlobal && this.model.removeAllGlobalRolesOnAccessor();
  }

  /**
   * Get role option
   * @param  {Boolean} isGlobal
   * @return {Object}
   */
  getRoleOption(): any {
    return this.accessMode === CONSTANT.ACCESS_MODE.DASHBOARD
      ? {
          dashboardId: this.documentId,
          tenantId: this.tenantId,
          isGlobal: this.isGlobal
        }
      : {
          reportId: this.documentId,
          tenantId: this.tenantId,
          isGlobal: this.isGlobal
        };
  }
}



// WEBPACK FOOTER //
// ./ui/logic/Izenda.AccessStore.js